import {
  Create,
  Edit,
  List,
  Datagrid,
  TextField,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRedirect,
  useResourceContext,
} from 'react-admin';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { SponsoredContentTeaserForm } from './forms/sponsored-content-teasers';
import { useSearchParams } from 'react-router-dom';
import { useTransformWithCampaignId } from './forms/hooks/useTransformWithCampaignId';
import {
  resolveCreativeParentLineItemShowPath,
  withPlacementsLineItemIdFromNativeForEdit,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

// return empty tool bar to keep the spacing consistent
const SponsoredContentTeaserListActions = () => <TopToolbar />;

export const SponsoredContentTeasersList = () => (
  <List actions={<SponsoredContentTeaserListActions />}>
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="placementsLineItemId" />
      <TextField source="title" />
      <TextField source="contentId" />
      <ESTDateField source="startsAt" />
      <ESTDateField source="endsAt" />
    </Datagrid>
  </List>
);

interface SponsoredContentTeaserCreateInitialValues {
  placementsLineItemId: string;
}

export const SponsoredContentTeaserCreate = () => {
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const placementsLineItemId = searchParams.get('placementsLineItemId') || '';
  const transform = useTransformWithCampaignId();

  const resource = useResourceContext();

  const record: SponsoredContentTeaserCreateInitialValues = {
    placementsLineItemId,
  };

  // The legacy /line_items show page can no longer load (PIO API shut off), so
  // resolve a legacy numeric id to its native UUID and land on /lineItems.
  // Prefer the submitted selection over the seeding query param — the user may
  // have picked a different line item in the form.
  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved new Sponsored Content Teaser');
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, { placementsLineItemId });
    redirect(path ?? 'list', resource);
  };

  return (
    <Create record={record} redirect="list" transform={transform} mutationOptions={{ onSuccess }}>
      <SponsoredContentTeaserForm type="Create" />
    </Create>
  );
};

export const SponsoredContentTeaserEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved Sponsored Content Teaser');
    // MD-5145: resolve the parent line item from the edited record (submitted data /
    // previousData), not the mutation response. All rows route to the native
    // /lineItems show page; a legacy-only id is translated via externalPioId.
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, variables?.previousData, data);
    redirect(path ?? 'list', resource);
  };

  return (
    <Edit
      redirect="list"
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      queryOptions={{ select: withPlacementsLineItemIdFromNativeForEdit }}
    >
      <SponsoredContentTeaserForm type="Update" />
    </Edit>
  );
};
